import { CommonModule, PlatformLocation, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import material from '@primeng/themes/material';
import * as localForage from 'localforage';
import * as _ from 'lodash';
import { providePrimeNG } from 'primeng/config';

import { AppModule } from './app/app.module';
import { AppAuthService } from './app/shared/common/auth/app-auth.service';
import { AppPreBootstrap } from './AppPreBootstrap';
import { EafModule, StorageService } from './assets/lib/eaf-ng2-module';
import { NgxBootstrapDatePickerConfigService } from './assets/lib/ngx-bootstrap/ngx-bootstrap-datepicker-config.service';
import { RootRoutingModule } from './root-routing.module';
import { RootComponent } from './root.component';
import { AppConsts } from './shared/AppConsts';
import { AppSessionService } from './shared/common/session/app-session.service';
import { CookieConsentService } from './shared/common/session/cookie-consent.service';
import { AppUiCustomizationService } from './shared/common/ui/app-ui-customization.service';
import { DomHelper } from './shared/helpers/DomHelper';
import { UrlHelper } from './shared/helpers/UrlHelper';
import { API_BASE_URL, UiCustomizationSettingsDto } from './shared/service-proxies/service-proxies';
import { ServiceProxyModule } from './shared/service-proxies/service-proxy.module';

// import nora from '@primeng/themes/nora';
// import lara from '@primeng/themes/lara';
// import aura from '@primeng/themes/aura';
export function appInitializerFactory(injector: Injector, platformLocation: PlatformLocation, storageService: StorageService) {
  return () => {
    eaf.ui.setBusy();
    return new Promise<boolean>((resolve, reject) => {
      AppConsts.appBaseHref = getBaseHref(platformLocation);
      const appBaseUrl = getDocumentOrigin() + AppConsts.appBaseHref;
      AppPreBootstrap.Init(storageService);
      AppPreBootstrap.run(
        appBaseUrl,
        () => {
          handleLogoutRequest(injector.get(AppAuthService));
          initializeLocalForage();

          const appSessionService: AppSessionService = injector.get(AppSessionService);
          appSessionService.init().then(
            result => {
              initializeAppCssClasses(injector, result);
              initializeTenantResources(injector);
              initializeCookieConsent(injector);
              registerLocales(resolve, reject);
            },
            err => {
              eaf.ui.clearBusy();
              reject(err);
            },
          );
        },
        resolve,
        reject,
      );
    });
  };
}

function initializeLocalForage() {
  try {
    // Tente inicializar o localForage
    if (localForage && typeof localForage.config === 'function') {
      localForage.config({
        driver: localForage.LOCALSTORAGE,
        name: 'OCCManager',
        version: 1.0,
        storeName: 'OCCManager',
        description: 'Cached data for OCCManager',
      });
    } else {
      // Fallback para localStorage
      console.warn('LocalForage not available, falling back to localStorage');
      // Você pode implementar funções auxiliares para localStorage se necessário
    }
  } catch (error) {
    console.error('Error initializing storage:', error);
  }
}

function initializeAppCssClasses(injector: Injector, theme: UiCustomizationSettingsDto) {
  const appUiCustomizationService = injector.get(AppUiCustomizationService);
  appUiCustomizationService.init(theme);

  //Css classes based on the layout
  if (eaf.session.userId) {
    document.body.className = appUiCustomizationService.getAppModuleBodyClass();
  } else {
    document.body.className = appUiCustomizationService.getAccountModuleBodyClass();
  }
}

function initializeTenantResources(injector: Injector) {
  const appSessionService: AppSessionService = injector.get(AppSessionService);

  const metaImage = DomHelper.getElementByAttributeValue('meta', 'property', 'og:image');
  if (metaImage) {
    //set og share image meta tag
    metaImage.setAttribute(
      'content',
      window.location.origin +
        '/assets/common/images/eaf/eaf-' +
        eaf.setting.get(appSessionService.theme.baseSettings.theme + '.' + 'App.UiManagement.Left.AsideSkin') +
        '.png',
    );
  }
}

function initializeCookieConsent(injector: Injector) {
  const cookieConsentService: CookieConsentService = injector.get(CookieConsentService);
  cookieConsentService.init();
}

function getDocumentOrigin() {
  if (!document.location.origin) {
    return document.location.protocol + '//' + document.location.hostname + (document.location.port ? ':' + document.location.port : '');
  }

  return document.location.origin;
}

function registerLocales(resolve: (value?: boolean | Promise<boolean>) => void, reject: any) {
  if (shouldLoadLocale()) {
    const angularLocale = convertEafLocaleToAngularLocale(eaf.localization.currentLanguage.name);

    // Usando importações dinâmicas com o formato correto para Angular 13+
    // Removendo a referência direta a ./locales
    switch (angularLocale) {
      case 'en':
        import('@angular/common/locales/en').then(module => {
          registerLocaleData(module.default);
          finishRegistration();
        }, handleError);
        break;
      case 'pt':
        import('@angular/common/locales/pt').then(module => {
          registerLocaleData(module.default);
          finishRegistration();
        }, handleError);
        break;
      case 'es':
        import('@angular/common/locales/es').then(module => {
          registerLocaleData(module.default);
          finishRegistration();
        }, handleError);
        break;
      // Adicione outros idiomas que você precisa suportar
      default:
        // Fallback para inglês
        import('@angular/common/locales/en').then(module => {
          registerLocaleData(module.default);
          finishRegistration();
        }, handleError);
        break;
    }
  } else {
    finishRegistration();
  }

  // Funções auxiliares para reduzir duplicação de código
  function finishRegistration() {
    NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales().then(_ => {
      resolve(true);
    });
  }

  function handleError(error) {
    console.error('Failed to load locale', error);
    // Tente carregar o inglês como fallback
    import('@angular/common/locales/en').then(
      module => {
        registerLocaleData(module.default);
        finishRegistration();
      },
      err => {
        console.error('Failed to load fallback locale', err);
        reject(err);
      },
    );
  }
}

export function shouldLoadLocale(): boolean {
  return eaf.localization.currentLanguage.name && eaf.localization.currentLanguage.name !== 'en-US';
}

export function convertEafLocaleToAngularLocale(locale: string): string {
  if (!AppConsts.localeMappings) {
    return locale;
  }

  const localeMapings = _.filter(AppConsts.localeMappings, { from: locale });
  if (localeMapings && localeMapings.length) {
    return localeMapings[0]['to'];
  }

  return locale;
}

export function getRemoteServiceBaseUrl(): string {
  return AppConsts.remoteServiceBaseUrl;
}

export function getCurrentLanguage(): string {
  return eaf.localization.currentLanguage.name;
}

export function getBaseHref(platformLocation: PlatformLocation): string {
  const baseUrl = platformLocation.getBaseHrefFromDOM();
  if (baseUrl) {
    return baseUrl;
  }

  return '/';
}

function handleLogoutRequest(authService: AppAuthService) {
  const currentUrl = UrlHelper.initialUrl;
  const returnUrl = UrlHelper.getReturnUrl();
  if (currentUrl.indexOf('account/logout') >= 0 && returnUrl) {
    authService.logout(true, returnUrl);
  }
}

@NgModule({
  imports: [BrowserModule, AppModule, CommonModule, EafModule, ServiceProxyModule, HttpClientModule, RootRoutingModule],
  declarations: [RootComponent],
  providers: [
    { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [Injector, PlatformLocation, StorageService],
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useFactory: getCurrentLanguage,
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: material,
        options: {
          darkModeSelector: false || 'none',
        },
      },
    }),
  ],
  bootstrap: [RootComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RootModule {}
